import {Badge, BottomNavigation, BottomNavigationAction, Box} from '@mui/material';
import {OccurrencesCount} from '../../../../components/occurrences/occurrences-count';
import {AccessTimeRounded, WarningRounded} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {translations} from '../../../../i18n/translations';
import HomeIcon from '@mui/icons-material/Home';
import {useAuth} from '../../../../contexts/auth/context';
import {ScreenPaths} from '../../../../enums';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useCanTrackAttendance} from '../../../../hooks/use-can-track-attendance';
import AttendanceOngoingBar from '../../../../components/attendance-tracker/attendance-ongoing-bar';
import {useAttendanceTrackerStatus} from '../../../../contexts/attendance-tracker-status/context';
import {useDailyCareLogFilters} from '../../../../contexts/dailycare-log-filters/context';
import moment from 'moment';

type DailyCareLogFooterProps = {
  bottomNavigationValue: number;
  setBottomNavigationValue: (value: number) => void;
};

export const DailyCareLogFooter = ({
  bottomNavigationValue,
  setBottomNavigationValue,
}: DailyCareLogFooterProps) => {
  const dailyCareLogContext = useDailyCareLog();
  const {t} = useTranslation();
  let history = useHistory();
  const {
    state: {session},
    hasReadPermission,
  } = useAuth();
  const {clearAllFilters} = useDailyCareLogFilters();
  const canTrackAttendance = useCanTrackAttendance();

  // A professional with an open session (an entry without exit) is reminded in the tab
  const hasOpenSession = !!useAttendanceTrackerStatus().openSession;

  return (
    <>
      <AttendanceOngoingBar />
      <BottomNavigation
        className="bottom-navigation"
        showLabels
        value={
          dailyCareLogContext.state.bottomNavigation.active ? bottomNavigationValue : undefined
        }
        onChange={(event, newValue) => setBottomNavigationValue(newValue)}
      >
        <BottomNavigationAction
          className="bottom-navigation-action"
          value={0}
          label={t(translations.daily_care_log.tabs.home)}
          icon={<HomeIcon />}
          onClick={() => {
            clearAllFilters();
            const searchParams = new URLSearchParams(window.location.search);
            const date = searchParams.get('date') ?? moment().format('YYYY-MM-DD');

            if (bottomNavigationValue === 0) {
              history.push(
                `${ScreenPaths.DailyCareLog}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Home}?date=${date}`
              );
            } else {
              history.push(
                `${ScreenPaths.DailyCareLog}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Home}?date=${date}`
              );
            }
          }}
        />
        {hasReadPermission(RolePermissionType.DailyCareLog_Occurrences) &&
          session?.selectedAffiliation?.contractsOverview?.includesDigitalCareManagement && (
            <BottomNavigationAction
              className="bottom-navigation-action"
              value={1}
              label={t(translations.daily_care_log.tabs.occurrences)}
              icon={
                <Box sx={{position: 'relative'}}>
                  <OccurrencesCount isActive={bottomNavigationValue === 1} />
                  <WarningRounded />
                </Box>
              }
              onClick={() => {
                history.push(
                  `${ScreenPaths.DailyCareLog}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Occurrences}?date=${moment().format('YYYY-MM-DD')}`
                );
              }}
            />
          )}
        {canTrackAttendance && (
          <BottomNavigationAction
            className="bottom-navigation-action"
            value={2}
            label={t(translations.daily_care_log.tabs.attendance_tracker)}
            icon={
              <Badge color="error" variant="dot" invisible={!hasOpenSession}>
                <AccessTimeRounded />
              </Badge>
            }
            onClick={() => {
              history.push(
                `${ScreenPaths.DailyCareLog}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_AttendanceTracker}?date=${moment().format('YYYY-MM-DD')}`
              );
            }}
          />
        )}
      </BottomNavigation>
    </>
  );
};
